import { Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import type { ComponentPropsWithRef } from 'react'
import type { RecordOrigin } from '~/routes/_authenticated/logistics/-record-origin'

/**
 * Retour d'une fiche chauffeur ou camion vers l'écran d'où on l'a ouverte (LU-181). Le chemin ne
 * donne que l'identifiant : le libellé nomme le genre d'écran, sans le nom de la tournée ou du hub.
 */
const ORIGIN_LABELS: Record<RecordOrigin['kind'], string> = {
  simulation: 'Retour à la tournée',
  operations: 'Retour à la tournée',
  hub: 'Retour au hub',
}

/**
 * Lien vers l'écran d'origine, sur la route de son genre, avec sa recherche (filtres, onglet du
 * détail) déjà revalidée par `recordOriginOf`. Les autres props vont au lien (rendu d'un `Button`).
 */
export function RecordOriginLink({
  origin,
  ...props
}: { origin: RecordOrigin } & Omit<ComponentPropsWithRef<'a'>, 'href'>) {
  switch (origin.kind) {
    case 'simulation': {
      return (
        <Link
          {...props}
          to="/logistics/simulations/$simulationId"
          params={{ simulationId: String(origin.id) }}
          search={origin.search}
        />
      )
    }
    case 'operations': {
      return (
        <Link
          {...props}
          to="/logistics/operations/$tourId"
          params={{ tourId: String(origin.id) }}
          search={origin.search}
        />
      )
    }
    case 'hub': {
      return (
        <Link
          {...props}
          to="/logistics/hubs/$hubId"
          params={{ hubId: origin.id }}
          search={origin.search}
        />
      )
    }
  }
}

/** « ‹ Retour à la tournée », en tête de la fiche, à gauche du fil d'Ariane. */
export function RecordOriginBackLink({ origin }: { origin: RecordOrigin }) {
  return (
    <RecordOriginLink
      origin={origin}
      className="flex shrink-0 items-center gap-0.5 text-xs leading-[14px] font-semibold text-primary-500 hover:underline"
    >
      <ChevronLeft className="size-3.5" aria-hidden="true" />
      {ORIGIN_LABELS[origin.kind]}
    </RecordOriginLink>
  )
}
